<template>
  <div v-if="!item.hidden && hasPermission">
    <!-- 有子菜单的情况 -->
    <el-sub-menu
      v-if="hasChildren"
      :index="resolvePath"
      popper-append-to-body
    >
      <template #title>
        <el-icon v-if="getMenuIcon(item)">
          <component :is="getMenuIcon(item)" />
        </el-icon>
        <span v-if="!isCollapse">{{ getMenuTitle(item) }}</span>
      </template>
      <sidebar-item
        v-for="child in item.children"
        :key="child.menuCode"
        :item="child"
        :base-path="resolvePath"
        :is-collapse="false"
      />
    </el-sub-menu>
    
    <!-- 没有子菜单但有页面的情况 -->
    <el-menu-item
      v-else-if="item.menuPage"
      :index="resolvePath"
    >
      <el-icon v-if="getMenuIcon(item)">
        <component :is="getMenuIcon(item)" />
      </el-icon>
      <span v-if="!isCollapse">{{ getMenuTitle(item) }}</span>
    </el-menu-item>
    
    <!-- 没有子菜单也没有页面的情况（仅作为分组标题） -->
    <div
      v-else
      class="menu-group-title"
      :class="{ 'is-collapse': isCollapse }"
    >
      <el-icon v-if="getMenuIcon(item)">
        <component :is="getMenuIcon(item)" />
      </el-icon>
      <span v-if="!isCollapse">{{ getMenuTitle(item) }}</span>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import path from 'path-browserify'
import { hasMenuPermission } from '@/utils/menu'

export default {
  name: 'SidebarItemComponent',
  props: {
    item: {
      type: Object,
      required: true
    },
    basePath: {
      type: String,
      default: ''
    },
    isCollapse: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { t } = useI18n()
    const store = useStore()
    
    const hasChildren = computed(() => {
      return props.item.children && props.item.children.length > 0
    })
    
    // 检查菜单权限
    const hasPermission = computed(() => {
      const userPermissions = store.state.user.menuPermissions
      return hasMenuPermission(props.item.menuCode, userPermissions)
    })
    
    const resolvePath = computed(() => {
      if (props.item.menuPage) {
        return path.resolve(props.basePath, props.item.menuCode.toLowerCase())
      }
      return props.basePath + '/' + props.item.menuCode.toLowerCase()
    })
    
    const getMenuIcon = (item) => {
      // 根据menuCode获取图标 - 使用Element Plus确实存在的图标
      const iconMap = {
        'INDEX': 'House',                    // 首页
        'DATAM': 'Monitor',                  // 设备管理
        'EQUM': 'Cpu',                       // 设备&滤芯库
        'LVXM': 'Refresh',                   // 换芯记录
        'UIVERSION': 'Upload',               // 家用版本库
        'DATAQ': 'Search',                   // TCP数据查询
        'HEART': 'TrendCharts',                    // 心跳上报数据
        'CUSTOM': 'User',                    // 单次使用数据
        'EQUACT': 'TrendCharts',             // 绑定信息统计
        'CRMM': 'OfficeBuilding',            // 甲方库
        'ORGM': 'User',                      // 客户管理
        'AGENTCHECK': 'Check',               // 代理商审核
        'AGENTCPS': 'Money',                 // 代理商分润管理
        'AGENTROUTE': 'Guide',               // 推广路线配置
        'PARTY_A': 'Grid',                   // 甲方类型管理
        'MQTTM': 'Connection',               // MQTT数据查询
        'STATUSM': 'View',                   // 状态查询
        'OUTORDERM': 'Document',             // 出水记录查询
        'DEBUGER': 'Tools',                  // 设备调试页面
        'WATERTTL': 'TrendCharts',                 // 出水总量查询
        'ACCTM': 'Setting',                  // 系统管理
        'ACCTMA': 'User',                    // 管理员帐号
        'TYPEM': 'Grid',                     // 设备类型管理
        'SETM': 'Tools',                     // 系统默认设置
        'VOLMAP': 'Location',                // 出水流量个性化配置
        'WATER_CONFIG': 'Setting',           // 温度水量配置
        'BUSSLOG': 'Document',               // 业务日志
        'WX_PAY': 'Money',                   // 支付管理
        'WX_PAY_ORDER': 'List',              // 订单管理
        'WX_PAY_STAT': 'DataAnalysis',       // 数据统计
        'WX_PAY_WATER_CONFIG': 'Setting',    // 水量价格配置
        'WX_PAY_WATER_RECHARGE': 'Wallet'    // 充值
      }
      return iconMap[item.menuCode] || 'Menu'
    }
    
    const getMenuTitle = (item) => {
      // 根据menuCode获取多语言标题
      const titleMap = {
        'INDEX': 'admin.dashboard',
        'DATAM': 'device.title',
        'EQUM': 'menu.equipment',
        'LVXM': 'menu.filterRecord',
        'UIVERSION': 'menu.version',
        'DATAQ': 'menu.dataQuery',
        'HEART': 'menu.heartbeat',
        'CUSTOM': 'menu.singleUse',
        'EQUACT': 'menu.bindStats',
        'CRMM': 'org.title',
        'ORGM': 'menu.customer',
        'AGENTCHECK': 'menu.agentCheck',
        'AGENTCPS': 'menu.agentProfit',
        'AGENTROUTE': 'menu.promotionRoute',
        'PARTY_A': 'menu.partyType',
        'MQTTM': 'menu.mqttQuery',
        'STATUSM': 'menu.statusQuery',
        'OUTORDERM': 'menu.outputRecord',
        'DEBUGER': 'menu.debug',
        'WATERTTL': 'menu.waterTotal',
        'ACCTM': 'setting.title',
        'ACCTMA': 'menu.adminAccount',
        'TYPEM': 'menu.deviceType',
        'SETM': 'menu.defaultSettings',
        'VOLMAP': 'menu.flowConfig',
        'WATER_CONFIG': 'menu.waterConfig',
        'BUSSLOG': 'menu.businessLog',
        'WX_PAY': 'menu.payment',
        'WX_PAY_ORDER': 'menu.order',
        'WX_PAY_STAT': 'menu.statistics',
        'WX_PAY_WATER_CONFIG': 'menu.waterPrice',
        'WX_PAY_WATER_RECHARGE': 'menu.recharge'
      }
      
      const titleKey = titleMap[item.menuCode]
      if (titleKey) {
        return t(titleKey)
      }
      
      // 如果没有找到对应的翻译，直接返回menuName
      return item.menuName
    }
    
    return {
      hasChildren,
      hasPermission,
      resolvePath,
      getMenuTitle,
      getMenuIcon
    }
  }
}
</script>

<style lang="scss" scoped>
.menu-group-title {
  padding: 0 20px;
  height: 50px;
  line-height: 50px;
  color: #bfcbd9;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  transition: all 0.3s;
  
  .el-icon {
    margin-right: 8px;
    font-size: 16px;
  }
  
  &.is-collapse {
    padding: 0 20px;
    justify-content: center;
    
    .el-icon {
      margin-right: 0;
    }
  }
}
</style>
